import React, { useEffect, useMemo, useRef, useState } from 'react';
import { es } from 'date-fns/locale';
import { CalendarX2, Loader2, Lock, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Calendar } from '../ui/calendar';
import { Skeleton } from '../ui/skeleton';
import { freeSlots, listBloqueos, primerHueco, toMin, dateToStr, strToDate } from '../../lib/citasApi';

/**
 * Selector reutilizable de hueco libre (día + hora) para dar/mover citas.
 *
 * Props:
 *  - agendaId:        id de la agenda (requerido para cargar huecos)
 *  - conceptCode:     código del concepto de cita (requerido)
 *  - durationMinutes: fuerza duración distinta a la del concepto (opcional)
 *  - excludeCitaId:   ignora esa cita al calcular huecos — para mover (opcional)
 *  - patientId:       no ofrece huecos que dejarían a ese paciente en dos sitios
 *                     a la vez (una de quirófano; 23/9/2026) — opcional
 *  - value:           {date: 'YYYY-MM-DD', start: 'HH:MM'} | null
 *  - onChange:        callback con el hueco elegido ({date, start}) o null si
 *                     el usuario cambia de día y la hora anterior deja de valer
 *  - onDiaBloqueado:  callback con el cierre de DÍA COMPLETO del día elegido
 *                     (doc del bloqueo) o null — para que el diálogo que usa
 *                     el picker corte antes de guardar (opcional)
 *
 * UI: calendario (react-day-picker, locale es) a la izquierda; chips de horas
 * agrupadas en Mañana (<15:00) y Tarde (>=15:00) a la derecha.
 */

const CUTOFF_TARDE_MIN = 15 * 60; // 15:00

function SlotChip({ slot, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`free-slot-chip-${slot.start}`}
      className={`px-3 py-1.5 rounded-md border text-sm tabular-nums transition-colors ${
        selected
          ? 'bg-[#2E6E5E] text-white border-[#2E6E5E] font-medium'
          : 'bg-white text-[#13231E] border-[#dbe3ee] hover:bg-[rgba(46,110,94,0.10)] hover:border-[#2E6E5E]'
      }`}
    >
      {slot.start}
    </button>
  );
}

function SlotGroup({ title, slots, selectedStart, onPick }) {
  if (slots.length === 0) return null;
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {slots.map((s) => (
          <SlotChip key={s.start} slot={s} selected={selectedStart === s.start} onClick={() => onPick(s)} />
        ))}
      </div>
    </div>
  );
}

/** Hora manual: teclear una hora cualquiera en vez de elegir un chip.
 *  El backend valida solapes y horario (y el admin puede forzar), así que
 *  aquí solo se construye el mismo {date, start} que un chip. */
function ManualHourRow({ dateStr, selectedStart, onChange }) {
  const [manual, setManual] = useState('');
  const isSelected = !!manual && selectedStart === manual;
  return (
    <div className="flex items-center gap-2 pt-2 border-t border-[#EEF3F0]" data-testid="free-slot-manual">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">Otra hora</span>
      <input
        type="time"
        value={manual}
        onChange={(e) => setManual(e.target.value)}
        className="h-8 rounded-md border border-[#dbe3ee] bg-white px-2 text-sm tabular-nums"
        data-testid="free-slot-manual-input"
      />
      <button
        type="button"
        disabled={!manual}
        onClick={() => onChange?.({ date: dateStr, start: manual })}
        data-testid="free-slot-manual-usar"
        className={`px-3 py-1.5 rounded-md border text-sm transition-colors disabled:opacity-50 ${
          isSelected
            ? 'bg-[#2E6E5E] text-white border-[#2E6E5E] font-medium'
            : 'bg-white text-[#13231E] border-[#dbe3ee] hover:bg-[rgba(46,110,94,0.10)] hover:border-[#2E6E5E]'
        }`}
      >
        {isSelected ? `${manual} ✓` : 'Usar esta hora'}
      </button>
    </div>
  );
}

function SlotsSkeleton() {
  return (
    <div className="space-y-3" data-testid="free-slot-loading">
      <Skeleton className="h-3 w-16" />
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-14 rounded-md" />
        ))}
      </div>
      <Skeleton className="h-3 w-16" />
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-14 rounded-md" />
        ))}
      </div>
    </div>
  );
}

export default function FreeSlotPicker({ agendaId, conceptCode, durationMinutes, excludeCitaId, patientId, value, onChange, onDiaBloqueado }) {
  // Día seleccionado (string YYYY-MM-DD). Se inicializa desde value si viene.
  const [dateStr, setDateStr] = useState(value?.date || null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Cierre de DÍA COMPLETO del día mirado: {date, doc}. Guarda el día al que
  // pertenece para no pintar en rojo el día siguiente mientras carga el nuevo.
  const [bloqueo, setBloqueo] = useState(null);
  const [cargandoBloqueo, setCargandoBloqueo] = useState(false);
  // Búsqueda del primer hueco disponible (escaneo en el servidor).
  const [buscandoPrimero, setBuscandoPrimero] = useState(false);
  // Evita aplicar respuestas fuera de orden si el usuario cambia rápido de día.
  const requestSeq = useRef(0);
  const bloqueoSeq = useRef(0);
  // El callback del padre suele venir como flecha inline (cambia en cada
  // render): se guarda en una ref para no meterlo en las dependencias.
  const avisarBloqueo = useRef(onDiaBloqueado);
  avisarBloqueo.current = onDiaBloqueado;

  const irAlPrimerHueco = async () => {
    if (!agendaId || !conceptCode || buscandoPrimero) return;
    setBuscandoPrimero(true);
    try {
      const r = await primerHueco({ agendaId, conceptCode, durationMinutes, excludeCitaId, patientId });
      if (r?.found) {
        // Selecciona el día en el calendario Y la hora directamente.
        setDateStr(r.date);
        onChange?.({ date: r.date, start: r.start });
      } else {
        toast.error('No hay huecos libres en los próximos 90 días');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo buscar el primer hueco');
    } finally {
      setBuscandoPrimero(false);
    }
  };

  // Si el padre cambia el value desde fuera (p.ej. edición), sincronizamos el día.
  useEffect(() => {
    if (value?.date && value.date !== dateStr) setDateStr(value.date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.date]);

  useEffect(() => {
    if (!agendaId || !conceptCode || !dateStr) {
      setSlots([]);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    freeSlots({ agendaId, date: dateStr, conceptCode, durationMinutes, excludeCitaId, patientId })
      .then((data) => {
        if (seq !== requestSeq.current) return;
        setSlots(Array.isArray(data?.slots) ? data.slots : []);
      })
      .catch((e) => {
        if (seq !== requestSeq.current) return;
        setSlots([]);
        setError(e?.response?.data?.detail || 'No se pudieron cargar los huecos');
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [agendaId, conceptCode, dateStr, durationMinutes, excludeCitaId, patientId]);

  // AGENDA BLOQUEADA EL DÍA ENTERO: ese día no se cita NI se mueve nada
  // (2026-09-17). El endpoint de huecos ya no ofrece chips
  // los días con un cierre `all_day`, pero entonces caía la pantalla de «No
  // hay huecos ese día» con la casilla de teclear una hora a mano, y por ahí
  // se colaba una cita en el día del congreso. Aquí se dice el motivo y no se
  // ofrece ninguna hora. Los cierres de TRAMO horario no entran: esos siguen
  // como siempre (se puede teclear la hora y el servidor decide).
  useEffect(() => {
    if (!agendaId || !dateStr) {
      setBloqueo(null);
      avisarBloqueo.current?.(null);
      return;
    }
    const seq = ++bloqueoSeq.current;
    setCargandoBloqueo(true);
    listBloqueos(agendaId, { dateFrom: dateStr, dateTo: dateStr })
      .then((items) => {
        if (seq !== bloqueoSeq.current) return;
        const cierre = (Array.isArray(items) ? items : []).find(
          (b) => (b.tipo || 'cierre') !== 'apertura' && b.all_day,
        ) || null;
        setBloqueo({ date: dateStr, doc: cierre });
        avisarBloqueo.current?.(cierre);
      })
      .catch(() => {
        // Si no se puede saber, no se inventa: la pantalla sigue como siempre
        // y el que de verdad corta es el servidor (PUT /api/citas).
        if (seq !== bloqueoSeq.current) return;
        setBloqueo(null);
        avisarBloqueo.current?.(null);
      })
      .finally(() => {
        if (seq === bloqueoSeq.current) setCargandoBloqueo(false);
      });
  }, [agendaId, dateStr]);

  const onSelectDay = (day) => {
    if (!day) return;
    const str = dateToStr(day);
    setDateStr(str);
    // La hora elegida en otro día deja de ser válida.
    if (value && value.date !== str) onChange?.(null);
  };

  const { manana, tarde } = useMemo(() => {
    const m = [];
    const t = [];
    for (const s of slots) (toMin(s.start) < CUTOFF_TARDE_MIN ? m : t).push(s);
    return { manana: m, tarde: t };
  }, [slots]);

  const selectedStart = value && value.date === dateStr ? value.start : null;
  const ready = !!(agendaId && conceptCode);
  // Solo vale el cierre del día que se está mirando ahora mismo.
  const cierreDelDia = bloqueo && bloqueo.date === dateStr ? bloqueo.doc : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="flex flex-col sm:flex-row gap-4" data-testid="free-slot-picker">
      <div className="shrink-0 self-start">
        <div className="rounded-lg border border-[#D9E3DD] bg-white">
          <Calendar
            mode="single"
            locale={es}
            selected={dateStr ? strToDate(dateStr) : undefined}
            onSelect={onSelectDay}
            disabled={{ before: today }}
            defaultMonth={dateStr ? strToDate(dateStr) : undefined}
          />
        </div>
        {/* Phase 27.11 — salta al primer hueco libre (día + hora) */}
        <button
          type="button"
          onClick={irAlPrimerHueco}
          disabled={!ready || buscandoPrimero}
          data-testid="free-slot-primero"
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-[#2E6E5E] bg-[rgba(46,110,94,0.08)] px-3 py-2 text-sm font-medium text-[#13231E] hover:bg-[rgba(46,110,94,0.16)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Busca el primer día con hueco libre y lo selecciona"
        >
          {buscandoPrimero
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Zap className="w-4 h-4 text-[#2E6E5E]" />}
          Primer hueco disponible
        </button>
      </div>

      <div className="flex-1 min-w-0 sm:min-h-[260px]">
        {!ready ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-4 text-center">
            Elige primero la agenda y el tipo de cita.
          </div>
        ) : !dateStr ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-4 text-center">
            Selecciona un día en el calendario para ver los huecos libres.
          </div>
        ) : loading || cargandoBloqueo ? (
          <SlotsSkeleton />
        ) : cierreDelDia ? (
          <div
            className="h-full flex flex-col items-center justify-center text-center p-4"
            data-testid="free-slot-bloqueado"
          >
            <Lock className="w-8 h-8 text-[#B91C1C] mb-2" />
            <div className="text-sm font-semibold text-[#B91C1C]">
              Agenda bloqueada: {cierreDelDia.reason || 'sin motivo'}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Ese día no se pueden dar citas. Si hace falta, quita antes el bloqueo.
            </div>
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-sm text-red-700 p-4 text-center">{error}</div>
        ) : slots.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-4" data-testid="free-slot-empty">
            <CalendarX2 className="w-8 h-8 text-[#2E6E5E] mb-2" />
            <div className="text-sm font-medium text-[#13231E]">No hay huecos ese día</div>
            <div className="text-xs text-muted-foreground mt-0.5">Prueba con otro día del calendario o teclea una hora.</div>
            <div className="mt-3 w-full max-w-xs">
              <ManualHourRow dateStr={dateStr} selectedStart={selectedStart} onChange={onChange} />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <SlotGroup title="Mañana" slots={manana} selectedStart={selectedStart} onPick={(s) => onChange?.({ date: dateStr, start: s.start })} />
            <SlotGroup title="Tarde" slots={tarde} selectedStart={selectedStart} onPick={(s) => onChange?.({ date: dateStr, start: s.start })} />
            <ManualHourRow dateStr={dateStr} selectedStart={selectedStart} onChange={onChange} />
          </div>
        )}
      </div>
    </div>
  );
}
